/**
 * Command Center charts (Spec 201 — US5) — best-practice backup visualizations.
 *
 * - BackupActivityTrend: stacked succeeded/failed runs per day (reliability over time).
 * - DonutStat: a donut with a centered headline number (coverage / readiness).
 * Built with recharts + design tokens per the blueprint (chartTooltipClasses for tips).
 *
 * Author: Peter Dobler (Dobler Data Solutions) · Feature 201
 */
import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, ComposedChart, Line,
} from 'recharts';
import { chartTooltipClasses } from '@/components/ui';
import type { TrendPoint } from '../../api/dashboard';

// Status palette (consistent across the command center).
export const COLOR = {
  succeeded: '#10b981', // emerald-500
  partial: '#f59e0b',   // amber-500 — Spec 214 FR-013: Partial is a first-class series
  failed: '#ef4444',    // red-500
  cancelled: '#94a3b8', // slate-400 — neutral: cancelled is an outcome, not a fault
  protected: '#10b981',
  unprotected: '#94a3b8', // slate-400
  onTrack: '#10b981',
  atRisk: '#f59e0b',    // amber-500
  accent: '#6366f1',    // indigo-500 (storage/data)
} as const;

// Per-item-type palette for the workspace-DR captured-items chart.
const TYPE_COLORS = ['#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#a855f7', '#ec4899', '#14b8a6', '#94a3b8'];

const mmdd = (iso: string) => (iso.length >= 10 ? `${iso.slice(5, 7)}/${iso.slice(8, 10)}` : iso);

/** Compact bytes formatter shared by the storage charts/panels. */
export const fmtBytes = (n: number | null | undefined): string => {
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let v = num;
  let i = 0;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${u[i]}`;
};

// Tooltip splits each day's succeeded/failed by lakehouse / warehouse / workspace so the numbers
// reflect the full picture (all three backup types feed the bars).
const TrendTooltip: React.FC<{ active?: boolean; payload?: unknown[]; label?: string }> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const row = (payload[0] as { payload: Record<string, number> }).payload;
  const types = [
    { name: 'Lakehouse', s: row.lhS, p: row.lhP, f: row.lhF },
    { name: 'Warehouse', s: row.whS, p: row.whP, f: row.whF },
    { name: 'Workspace', s: row.wsS, p: row.wsP, f: row.wsF },
  ].filter((t) => (t.s || 0) + (t.p || 0) + (t.f || 0) > 0);
  return (
    <div className={chartTooltipClasses}>
      <p className="mb-1 font-medium text-[var(--color-text-primary)]">{label}</p>
      {types.length === 0 ? (
        <p className="text-xs text-[var(--color-text-secondary)]">No runs</p>
      ) : (
        types.map((t) => (
          <p key={t.name} className="text-xs text-[var(--color-text-secondary)]">
            {t.name}: <span style={{ color: COLOR.succeeded }}>{t.s || 0} ok</span>
            {(t.p || 0) > 0 && <> · <span style={{ color: COLOR.partial }}>{t.p} partial</span></>}
            {(t.f || 0) > 0 && <> · <span style={{ color: COLOR.failed }}>{t.f} failed</span></>}
          </p>
        ))
      )}
      <p className="mt-1 text-xs font-medium text-[var(--color-text-primary)]">
        Total: {row.Succeeded || 0} ok · {row.Partial || 0} partial · {row.Failed || 0} failed
      </p>
    </div>
  );
};

export const BackupActivityTrend: React.FC<{ data: TrendPoint[]; height?: number }> = ({ data, height = 260 }) => {
  const empty = !data || data.every((d) => d.total === 0);
  if (empty) {
    return (
      <div className="flex h-[260px] items-center justify-center text-sm text-[var(--color-text-secondary)]">
        No backup activity in this window yet.
      </div>
    );
  }
  // Spec 214 FR-013: Partial is its own stacked series — it used to appear in NO bar
  // (silently vanishing from the chart while deflating the success rate).
  const rows = data.map((d) => ({
    date: mmdd(d.date), Succeeded: d.succeeded, Partial: d.partial ?? 0, Failed: d.failed,
    lhS: d.lhSucceeded, lhP: d.lhPartial ?? 0, lhF: d.lhFailed,
    whS: d.whSucceeded, whP: d.whPartial ?? 0, whF: d.whFailed,
    wsS: d.wsSucceeded, wsP: d.wsPartial ?? 0, wsF: d.wsFailed,
  }));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={rows} margin={{ top: 5, right: 12, left: -12, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
        <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'var(--color-text-secondary)' }} interval="preserveStartEnd" />
        <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'var(--color-text-secondary)' }} />
        <Tooltip cursor={{ fill: 'var(--color-bg-subtle)' }} content={<TrendTooltip />} />
        <Legend formatter={(v: string) => <span className="text-xs text-[var(--color-text-secondary)]">{v}</span>} />
        <Bar dataKey="Succeeded" stackId="a" fill={COLOR.succeeded} radius={[0, 0, 0, 0]} />
        <Bar dataKey="Partial" stackId="a" fill={COLOR.partial} radius={[0, 0, 0, 0]} />
        <Bar dataKey="Failed" stackId="a" fill={COLOR.failed} radius={[3, 3, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
};

/** Workspace-DR: captured item count by Fabric type (horizontal bars). */
export const ItemTypeBar: React.FC<{ data: Array<{ type: string; count: number }>; height?: number }> = ({ data, height }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex h-[180px] items-center justify-center text-sm text-[var(--color-text-secondary)]">
        No item definitions captured yet.
      </div>
    );
  }
  const rows = [...data].sort((a, b) => b.count - a.count);
  const h = height ?? Math.max(140, rows.length * 34 + 20);
  return (
    <ResponsiveContainer width="100%" height={h}>
      <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" horizontal={false} />
        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: 'var(--color-text-secondary)' }} />
        <YAxis type="category" dataKey="type" width={110} tick={{ fontSize: 11, fill: 'var(--color-text-secondary)' }} />
        <Tooltip
          cursor={{ fill: 'var(--color-bg-subtle)' }}
          content={({ active, payload }) =>
            active && payload?.length ? (
              <div className={chartTooltipClasses}>
                <p className="text-sm font-medium text-[var(--color-text-primary)]">{(payload[0].payload as { type: string }).type}</p>
                <p className="text-sm text-[var(--color-text-secondary)]">Items: <span className="font-semibold">{payload[0].value as number}</span></p>
              </div>
            ) : null
          }
        />
        <Bar dataKey="count" radius={[0, 3, 3, 0]}>
          {rows.map((r, i) => (
            <Cell key={r.type} fill={TYPE_COLORS[i % TYPE_COLORS.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

/** Storage: bytes backed up per day over the window (cumulative-feel area). */
export const StorageGrowthArea: React.FC<{ data: Array<{ date: string; bytes: number; forecast?: boolean }>; height?: number }> = ({ data, height = 220 }) => {
  const empty = !data || data.every((d) => d.bytes === 0);
  if (empty) {
    return (
      <div className="flex h-[220px] items-center justify-center text-sm text-[var(--color-text-secondary)]">
        No protected data yet.
      </div>
    );
  }
  // Cumulative protected footprint (solid) + projected trend (dashed). The forecast line is seeded
  // at the last actual point so the two segments join cleanly.
  const rows = data.map((d, i) => ({
    date: mmdd(d.date),
    actual: d.forecast ? null : d.bytes,
    forecast: d.forecast ? d.bytes : data[i + 1]?.forecast ? d.bytes : null,
  }));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={rows} margin={{ top: 5, right: 12, left: 4, bottom: 0 }}>
        <defs>
          <linearGradient id="storageFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={COLOR.accent} stopOpacity={0.35} />
            <stop offset="95%" stopColor={COLOR.accent} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
        <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'var(--color-text-secondary)' }} interval="preserveStartEnd" />
        <YAxis tickFormatter={(v: number) => fmtBytes(v)} width={64} tick={{ fontSize: 11, fill: 'var(--color-text-secondary)' }} />
        <Tooltip
          cursor={{ fill: 'var(--color-bg-subtle)' }}
          content={({ active, payload, label }) => {
            if (!active || !payload?.length) return null;
            const p = payload.find((x) => x.value != null);
            if (!p) return null;
            const isF = p.dataKey === 'forecast';
            return (
              <div className={chartTooltipClasses}>
                <p className="mb-1 font-medium text-[var(--color-text-primary)]">{label}</p>
                <p className="text-xs" style={{ color: COLOR.accent }}>
                  {isF ? 'Forecast' : 'Protected'}: {fmtBytes(p.value as number)}
                </p>
              </div>
            );
          }}
        />
        <Area type="monotone" dataKey="actual" stroke={COLOR.accent} strokeWidth={2} fill="url(#storageFill)" connectNulls />
        <Line type="monotone" dataKey="forecast" stroke={COLOR.accent} strokeWidth={2} strokeDasharray="5 5" dot={false} connectNulls />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

interface DonutSlice { name: string; value: number; color: string; [k: string]: string | number }

const DonutTooltip: React.FC<{ active?: boolean; payload?: unknown[] }> = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = (payload as Array<{ payload: DonutSlice }>)[0].payload;
  return (
    <div className={chartTooltipClasses}>
      <p className="text-sm font-medium text-[var(--color-text-primary)]">{d.name}</p>
      <p className="text-sm text-[var(--color-text-secondary)]">Count: <span className="font-semibold">{d.value}</span></p>
    </div>
  );
};

/** Donut with a centered headline (value + caption) and a compact legend. */
export const DonutStat: React.FC<{
  slices: DonutSlice[];
  centerValue: React.ReactNode;
  centerLabel: string;
  height?: number;
}> = ({ slices, centerValue, centerLabel, height = 220 }) => {
  const total = slices.reduce((s, x) => s + x.value, 0);
  if (total === 0) {
    return (
      <div className="flex h-[220px] items-center justify-center text-sm text-[var(--color-text-secondary)]">
        No data yet.
      </div>
    );
  }
  return (
    <div className="relative">
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie data={slices} cx="50%" cy="50%" innerRadius={62} outerRadius={92} paddingAngle={2} dataKey="value" nameKey="name" stroke="none">
            {slices.map((s) => (
              <Cell key={s.name} fill={s.color} />
            ))}
          </Pie>
          <Tooltip content={<DonutTooltip />} />
          <Legend
            verticalAlign="bottom"
            height={24}
            formatter={(v: string) => <span className="text-xs text-[var(--color-text-secondary)]">{v}</span>}
          />
        </PieChart>
      </ResponsiveContainer>
      {/* Centered headline overlay (recharts has no native center label). */}
      <div className="pointer-events-none absolute inset-x-0 top-[88px] flex flex-col items-center">
        <span className="text-2xl font-bold text-[var(--color-text-primary)]">{centerValue}</span>
        <span className="text-[11px] uppercase tracking-wide text-[var(--color-text-secondary)]">{centerLabel}</span>
      </div>
    </div>
  );
};
